import { useEffect, useState } from 'react';
import { ProductIdentity } from '@/components/products';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Check, Minus, Plus } from 'lucide-react';

export interface PicklistLineView {
  lineId: string;
  productId: string;
  productName?: string;
  productSku?: string;
  productImageUrl?: string | null;
  quantityNeeded: number;
  quantityAvailable: number;
  quantityToPick: number;
  quantityPicked: number | null;
  isPicked: boolean;
  isDeferred: boolean;
  slots: Array<{ slotLabel: string | null; slotNumber: number; quantityToLoad: number }>;
}

interface PicklistMobileViewProps {
  lines: PicklistLineView[];
  onPick: (lineId: string, quantity: number) => Promise<void>;
  pickingLineId?: string | null;
  onDefer?: (lineId: string, deferred: boolean) => Promise<void>;
  deferringLineId?: string | null;
  /** Hides pick controls, e.g. once the picklist's refill session has been dispatched. */
  readOnly?: boolean;
}

export function PicklistMobileView({
  lines,
  onPick,
  pickingLineId,
  onDefer,
  deferringLineId,
  readOnly,
}: PicklistMobileViewProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {lines.map((line) => (
          <PicklistLineCard
            key={line.lineId}
            line={line}
            onPick={onPick}
            picking={pickingLineId === line.lineId}
            onDefer={onDefer}
            deferring={deferringLineId === line.lineId}
            readOnly={readOnly}
          />
        ))}
      </div>
    </div>
  );
}

function PicklistLineCard({
  line,
  onPick,
  picking,
  onDefer,
  deferring,
  readOnly,
}: {
  line: PicklistLineView;
  onPick: (lineId: string, quantity: number) => Promise<void>;
  picking: boolean;
  onDefer?: (lineId: string, deferred: boolean) => Promise<void>;
  deferring: boolean;
  readOnly?: boolean;
}) {
  const alreadyPicked = line.quantityPicked ?? 0;
  const maxPick = Math.max(0, line.quantityAvailable - alreadyPicked);
  const remainingNeeded = Math.max(0, line.quantityNeeded - alreadyPicked);
  const defaultQty = Math.min(remainingNeeded, maxPick);
  const [qty, setQty] = useState(defaultQty);
  useEffect(() => {
    setQty(defaultQty);
  }, [defaultQty, alreadyPicked, maxPick]);
  const effectiveQty = Math.min(Math.max(0, qty), maxPick);
  const noStock = maxPick <= 0;
  const busy = picking || deferring;
  const deferred = line.isDeferred;
  const picked = line.isPicked;
  const canAdjustQty = !readOnly && !deferred && maxPick > 0;
  const showActions = !readOnly || picked;

  return (
    <Card className={cn(picked && 'border-2 border-emerald-500')}>
      <CardContent className="p-4 space-y-3">
        <ProductIdentity
          productId={line.productId}
          name={line.productName?.trim() || line.productSku?.trim() || line.productId}
          imageUrl={line.productImageUrl}
          showSku={false}
          popOut={false}
          className="min-w-0"
          trailing={
            deferred ? (
              <Badge variant="destructive" className="shrink-0">
                Deferred
              </Badge>
            ) : null
          }
        >
          <div
            className={cn(
              'grid gap-3',
              line.slots.length > 0 ? 'grid-cols-3' : 'grid-cols-2'
            )}
          >
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Needed
              </p>
              <p className="text-2xl font-semibold tabular-nums leading-tight">
                {line.quantityNeeded}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Avail
              </p>
              <p
                className={cn(
                  'text-2xl font-semibold tabular-nums leading-tight',
                  line.quantityAvailable <= 0 && 'text-destructive'
                )}
              >
                {line.quantityAvailable}
              </p>
            </div>
            {line.slots.length > 0 ? (
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {line.slots.length === 1 ? 'Slot' : 'Slots'}
                </p>
                <p className="text-2xl font-semibold tabular-nums leading-tight">
                  {line.slots.map((s) => s.slotLabel || s.slotNumber).join(', ')}
                </p>
              </div>
            ) : null}
          </div>
          {alreadyPicked > 0 && !picked ? (
            <div className="text-xs text-muted-foreground">
              Picked {alreadyPicked} of {line.quantityToPick} so far
            </div>
          ) : null}
        </ProductIdentity>
        {canAdjustQty ? (
          <div className="flex items-center justify-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={effectiveQty <= 0 || busy}
              onClick={() => setQty(Math.max(0, effectiveQty - 1))}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="min-w-8 text-center text-2xl font-semibold tabular-nums leading-tight">
              {effectiveQty}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled={effectiveQty >= maxPick || busy}
              onClick={() => setQty(Math.min(maxPick, effectiveQty + 1))}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
        {showActions && (
          <div className="flex gap-2">
            <Button
              className={cn(
                'flex-1',
                picked &&
                  effectiveQty <= 0 &&
                  'bg-emerald-600 text-white hover:bg-emerald-600 disabled:opacity-100'
              )}
              disabled={busy || deferred || noStock || effectiveQty <= 0}
              onClick={() => onPick(line.lineId, effectiveQty)}
            >
              {picking ? (
                'Picking…'
              ) : picked && effectiveQty <= 0 ? (
                <>
                  <Check className="h-4 w-4" />
                  Picked
                </>
              ) : picked ? (
                'Pick more'
              ) : (
                'Pick'
              )}
            </Button>
            {onDefer && !picked && (
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                disabled={busy}
                onClick={() => onDefer(line.lineId, !deferred)}
              >
                {deferring
                  ? deferred
                    ? 'Resuming…'
                    : 'Deferring…'
                  : deferred
                    ? 'Resume stocking'
                    : 'Defer'}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PicklistMobileView;
